/**
 * BI Cleanup Validation Page
 * Human review interface for transaction classification
 * 
 * Features:
 * - Transaction list with confidence indicators
 * - One-click approve/reclassify
 * - Bulk actions
 * - Progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import { useSwipeable } from 'react-swipeable';
import { Pencil, Clock, AlertTriangle, Download, Loader2, CheckCircle2, Zap } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { anomalySeverityStyle } from '@/utils/anomalySeverity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Types
interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  payee: string | null;
  classification: {
    category: string;
    subcategory: string | null;
    confidence: number;
    confidence_level: 'high' | 'medium' | 'low';
    model_used: string;
  };
  needs_review: boolean;
  validation_status: 'pending' | 'approved' | 'rejected' | 'modified';
  source: {
    filename: string;
    page_number: number;
  };
  learned?: boolean;  // Indicates if this category was learned from user correction
  learned_from?: string;  // Pattern it was learned from
  // Backend flag — set when the cleanup job auto-approved this transaction
  // based on the high-confidence rules in the classifier. Read at L152,
  // L338, L346. Restored 2026-04-29 (TS-FU3 batch 3) per archaeology
  // pattern §1 — the consumer code already reads it; the interface had
  // drifted away.
  auto_approved?: boolean;
}

interface JobStats {
  total_transactions: number;
  auto_approved: number;
  needs_review: number;
  reviewed: number;
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
  anomaly_count?: number;
  critical_anomalies?: number;
  high_anomalies?: number;
}

interface JobStatus {
  job_id: string;
  status: string;
  stats: JobStats;
  progress_pct: number;
  error: string | null;
  report_id?: string;
  report_name?: string;
  business_name?: string;
  bank_name?: string;
  statement_period?: string;
}

// Category options
const CATEGORIES = [
  "Rent & Occupancy",
  "Utilities",
  "Insurance",
  "Payroll & Benefits",
  "Professional Services",
  "Marketing & Advertising",
  "Technology & Software",
  "Office & Supplies",
  "Travel & Entertainment",
  "Vehicle & Transportation",
  "Inventory & Merchandise",
  "Food & Beverage Cost",
  "Manufacturing",
  "Sales Revenue",
  "Other Income",
  "Bank & Financial Fees",
  "Taxes & Licenses",
  "Debt Service",
  "Miscellaneous",
  "Transfer",
  // Exclude from claim (not a business expense)
  "------- EXCLUDE FROM CLAIM -------",
  "Owner Draw"
];

// Confidence badge component
function ConfidenceBadge({ level, confidence }: { level: string; confidence: number }) {
  // Use confidence percentage to determine color (more intuitive than level)
  const getColorStyle = (): React.CSSProperties => {
    const pct = confidence * 100;
    if (pct >= 90) return { background: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' };
    return { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
  };

  return (
    <span className="cs-chip px-2 py-1 rounded-full border" style={getColorStyle()}>
      {(confidence * 100).toFixed(0)}%
    </span>
  );
}

// Transaction row component
function TransactionRow({ 
  transaction, 
  selected,
  highlighted,
  onSelect,
  onApprove, 
  onReclassify 
}: { 
  transaction: Transaction;
  selected: boolean;
  highlighted?: boolean;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReclassify: (id: string, category: string) => void;
}) {
  const [showCategories, setShowCategories] = useState(false);
  
  const getStatusBadge = () => {
    // Check validation_status first, then fall back to auto_approved
    switch (transaction.validation_status) {
      case 'approved':
        return <span className="cs-chip px-2 py-1 rounded" style={{ background: 'transparent', color: 'var(--color-verified)' }}>✓ Approved</span>;
      case 'modified':
        return <span className="cs-chip px-2 py-1 rounded flex items-center gap-1 w-fit" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}><Pencil className="w-3 h-3" aria-hidden="true" /> Modified</span>;
      case 'rejected':
        return <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>✗ Rejected</span>;
      default:
        // If auto_approved but no validation_status, show as auto-approved
        if (transaction.auto_approved) {
          return <span className="cs-chip px-2 py-1 rounded" style={{ background: 'transparent', color: 'var(--color-verified)' }}>✓ Auto-Approved</span>;
        }
        return <span className="cs-chip px-2 py-1 rounded flex items-center gap-1 w-fit" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}><Clock className="w-3 h-3" aria-hidden="true" /> Needs Review</span>;
    }
  };

  return (
    <tr
      id={`txn-${transaction.id}`}
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--color-border-faint)',
        background: highlighted
          ? 'var(--color-needs-human-tint)'
          : selected
          ? 'var(--color-needs-human-tint)'
          : 'transparent',
        boxShadow: highlighted ? 'inset 4px 0 0 0 var(--color-brand)' : undefined,
      }}
      onMouseEnter={(e) => { if (!highlighted && !selected) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
      onMouseLeave={(e) => { if (!highlighted && !selected) e.currentTarget.style.background = 'transparent'; }}
    >
      <td className="py-3 px-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(transaction.id)}
          className="rounded"
          style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
        />
      </td>
      <td className="py-3 px-4 cs-meta">{transaction.date}</td>
      <td className="py-3 px-4">
        <div className="cs-body truncate max-w-xs" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }} title={transaction.description}>
          {transaction.description}
        </div>
        {transaction.payee && (
          <div className="cs-meta">{transaction.payee}</div>
        )}
      </td>
      <td className="py-3 px-4 cs-figure" style={{ color: transaction.amount < 0 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}>
        <FinancialValue value={transaction.amount} cents />
      </td>
      <td className="py-3 px-4">
        <div className="relative">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="cs-body underline cursor-pointer"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              {transaction.classification.category}
            </button>
            {/* Learned Badge - shows when category came from AI learning */}
            {transaction.learned && (
              <span
                className="cs-chip px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                title="Category learned from your previous corrections"
              >
                📚 Learned
              </span>
            )}
          </div>
          {showCategories && (
            <div className="absolute z-10 mt-1 w-56 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    onReclassify(transaction.id, cat);
                    setShowCategories(false);
                  }}
                  className="w-full text-left px-3 py-2 cs-body cursor-pointer"
                  style={cat === transaction.classification.category ? { background: 'var(--color-needs-human-tint)', fontWeight: 500 } : undefined}
                  onMouseEnter={(e) => { if (cat !== transaction.classification.category) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { if (cat !== transaction.classification.category) e.currentTarget.style.background = 'transparent'; }}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <ConfidenceBadge 
          level={transaction.classification.confidence_level} 
          confidence={transaction.classification.confidence}
        />
      </td>
      <td className="py-3 px-4">{getStatusBadge()}</td>
      <td className="py-3 px-4">
        {(transaction.validation_status === 'pending' || transaction.validation_status === null || !transaction.validation_status) && (
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(transaction.id)}
              className="px-3 py-1 cs-body rounded cursor-pointer"
              style={{ background: 'var(--color-verified)', color: 'var(--color-bg-base)' }}
            >
              Approve
            </button>
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="px-3 py-1 cs-body rounded cursor-pointer"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Edit
            </button>
          </div>
        )}
        {transaction.validation_status === 'approved' && (
          <span className="cs-body" style={{ color: 'var(--color-verified)' }}>✓ Approved</span>
        )}
        {transaction.validation_status === 'modified' && (
          <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>✓ Modified</span>
        )}
      </td>
    </tr>
  );
}

// Mobile Transaction Card with Swipe Gestures
function TransactionCard({ 
  transaction, 
  selected,
  highlighted,
  onSelect,
  onApprove, 
  onReclassify 
}: { 
  transaction: Transaction;
  selected: boolean;
  highlighted?: boolean;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReclassify: (id: string, category: string) => void;
}) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [showCategories, setShowCategories] = useState(false);
  const [swiping, setSwiping] = useState(false);
  
  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      setSwiping(true);
      setSwipeOffset(Math.max(-100, Math.min(100, e.deltaX)));
    },
    onSwipedLeft: () => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      if (swipeOffset < -60) {
        setShowCategories(true);
      }
      setSwipeOffset(0);
      setSwiping(false);
    },
    onSwipedRight: () => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      if (swipeOffset > 60) {
        onApprove(transaction.id);
      }
      setSwipeOffset(0);
      setSwiping(false);
    },
    onSwiped: () => {
      setSwipeOffset(0);
      setSwiping(false);
    },
    delta: 10,
    trackMouse: false,
    preventScrollOnSwipe: true
  });
  
  const getConfidenceStyle = (): React.CSSProperties => {
    // Use confidence percentage to determine color (more intuitive)
    const pct = transaction.classification.confidence * 100;
    if (pct >= 90) return { background: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' };
    return { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' };
  };

  const getStatusBadge = () => {
    switch (transaction.validation_status) {
      case 'approved':
        return <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'transparent', color: 'var(--color-verified)' }}>✓ Approved</span>;
      case 'modified':
        return <span className="cs-chip px-2 py-0.5 rounded-full flex items-center gap-1 w-fit" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}><Pencil className="w-3 h-3" aria-hidden="true" /> Modified</span>;
      case 'rejected':
        return <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>✗ Rejected</span>;
      default:
        // If auto_approved but no validation_status, show as auto-approved
        if (transaction.auto_approved) {
          return <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'transparent', color: 'var(--color-verified)' }}>✓ Auto</span>;
        }
        return <span className="cs-chip px-2 py-0.5 rounded-full flex items-center gap-1 w-fit" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}><Clock className="w-3 h-3" aria-hidden="true" /> Review</span>;
    }
  };
  
  // Consider auto-approved transactions as not pending
  const isPending = !transaction.auto_approved && (!transaction.validation_status || transaction.validation_status === 'pending');
  
  return (
    <div className="relative overflow-hidden">
      {/* Swipe Background Indicators */}
      {isPending && (
        <>
          <div className={`absolute inset-y-0 left-0 w-24 flex items-center justify-center transition-opacity ${swipeOffset > 30 ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'var(--color-verified)' }}>
            <span className="font-semibold" style={{ color: 'var(--color-bg-base)' }}>✓ Approve</span>
          </div>
          <div className={`absolute inset-y-0 right-0 w-24 flex items-center justify-center transition-opacity ${swipeOffset < -30 ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'var(--color-brand)' }}>
            <span className="font-semibold flex items-center gap-1" style={{ color: 'var(--color-on-brand)' }}>Edit <Pencil className="w-4 h-4" aria-hidden="true" /></span>
          </div>
        </>
      )}

      {/* Card Content */}
      <div
        id={`txn-${transaction.id}`}
        {...swipeHandlers}
        className={`p-4 border-b transition-transform ${swiping ? '' : 'transition-all duration-200'}`}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          borderColor: 'var(--color-border-sub)',
          background: highlighted
            ? 'var(--color-needs-human-tint)'
            : selected
            ? 'var(--color-needs-human-tint)'
            : 'var(--color-bg-surface)',
          boxShadow: highlighted ? 'inset 4px 0 0 0 var(--color-brand)' : undefined,
        }}
      >
        {/* Header Row: Date, Amount, Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onSelect(transaction.id)}
              className="rounded w-5 h-5"
              style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
            />
            <span className="cs-meta">{transaction.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="cs-figure font-semibold" style={{ fontSize: '1rem', color: transaction.amount < 0 ? 'var(--color-text-muted)' : 'var(--color-text-secondary)' }}>
              <FinancialValue value={transaction.amount} cents />
            </span>
            {getStatusBadge()}
          </div>
        </div>

        {/* Description */}
        <div className="mb-3">
          <p className="cs-body line-clamp-2" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{transaction.description}</p>
          {transaction.payee && (
            <p className="cs-meta mt-0.5">{transaction.payee}</p>
          )}
        </div>

        {/* Category & Confidence */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="cs-body font-medium flex items-center gap-1"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              {transaction.classification.category}
              <svg className={`w-4 h-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Learned Badge for mobile */}
            {transaction.learned && (
              <span className="cs-chip px-1.5 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                📚
              </span>
            )}
          </div>
          <span className="cs-chip px-2 py-1 rounded-full border" style={getConfidenceStyle()}>
            {(transaction.classification.confidence * 100).toFixed(0)}% confidence
          </span>
        </div>

        {/* Category Selector (Expandable) */}
        {showCategories && (
          <div className="mb-3 grid grid-cols-2 gap-2 p-3 rounded-lg max-h-48 overflow-y-auto" style={{ background: 'var(--color-bg-base)' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => {
                  onReclassify(transaction.id, cat);
                  setShowCategories(false);
                }}
                className="p-2 cs-meta text-left rounded-lg transition-colors border"
                style={
                  cat === transaction.classification.category
                    ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', fontWeight: 500, borderColor: 'var(--color-needs-human-tint-bd)', borderWidth: 2 }
                    : { background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }
                }
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Action Buttons - Only for pending transactions */}
        {isPending && (
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(transaction.id)}
              className="flex-1 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              style={{ background: 'var(--color-verified)', color: 'var(--color-bg-base)' }}
            >
              <span>✓</span> Approve
            </button>
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="flex-1 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Pencil className="w-4 h-4" aria-hidden="true" /> Edit Category
            </button>
          </div>
        )}

        {/* Swipe Hint - Only for pending */}
        {isPending && (
          <p className="cs-meta text-center mt-2 md:hidden" style={{ color: 'var(--color-text-dim)' }}>
            ← Swipe left to edit • Swipe right to approve →
          </p>
        )}
      </div>
    </div>
  );
}

// Main component
export default function CleanupValidation() {
  const { jobId: jobIdFromPath } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  
  // Resolved job ID - either from path or resolved from draft query param
  const [resolvedJobId, setResolvedJobId] = useState<string | null>(jobIdFromPath || null);
  const [resolving, setResolving] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'needs_review' | 'approved'>('all'); // Default to 'all' to show everything
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [showAnomalies, setShowAnomalies] = useState(true); // Default to showing anomalies
  const [showAllAnomalies, setShowAllAnomalies] = useState(false); // For expand/collapse more anomalies
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null); // For anomaly linking
  const [wizardContext, setWizardContext] = useState<{
    from_wizard: boolean;
    return_to: string;
    step: number;
    report_id?: string;
  } | null>(null);
  
  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [amountMin, setAmountMin] = useState<string>('');
  const [amountMax, setAmountMax] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Resolve job ID from draft query parameter if not provided in path
  useEffect(() => {
    const resolveJobFromDraft = async () => {
      // If we already have a job ID from path, use it
      if (jobIdFromPath) {
        setResolvedJobId(jobIdFromPath);
        return;
      }
      
      // Check for draft query parameter
      const draftId = searchParams.get('draft');
      if (!draftId) {
        setError('No job ID or draft ID provided');
        setLoading(false);
        return;
      }
      
      // Resolve job ID from draft/report ID
      setResolving(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/by-report/${draftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const resolvedId = response.data.job_id;
        console.log('🔵 Resolved cleanup job ID from draft:', draftId, '->', resolvedId);
        setResolvedJobId(resolvedId);
        
        // Optionally redirect to the canonical URL with jobId in path
        // navigate(`/cleanup/validation/${resolvedId}`, { replace: true });
      } catch (err: any) {
        console.error('Failed to resolve job from draft:', err);
        setError(err.response?.data?.detail || `No cleanup job found for this case. The case may not have any bank statements uploaded yet.`);
        setLoading(false);
      } finally {
        setResolving(false);
      }
    };
    
    resolveJobFromDraft();
  }, [jobIdFromPath, searchParams, getToken]);
  
  // Use resolved job ID throughout the component
  const jobId = resolvedJobId;
  
  // Check for wizard context on mount
  useEffect(() => {
    const contextStr = sessionStorage.getItem('wizard_context');
    if (contextStr) {
      try {
        const context = JSON.parse(contextStr);
        if (context.from_wizard) {
          setWizardContext(context);
        }
      } catch (e) {
        console.error('Failed to parse wizard context:', e);
      }
    }
  }, []);
  
  // Filter to only show unresolved anomalies
  const unresolvedAnomalies = anomalies.filter(a => !a.is_resolved);
  
  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);
  
  // Load job status
  const loadJobStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}`,
        { headers }
      );
      setJobStatus(response.data);
    } catch (err: any) {
      console.error('Failed to load job status:', err);
    }
  }, [jobId, getAuthHeader]);
  
  // Load anomalies
  const loadAnomalies = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/anomalies/${jobId}`,
        { headers }
      );
      setAnomalies(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Failed to load anomalies:', err);
    }
  }, [jobId, getAuthHeader]);
  
  // Load transactions
  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeader();
      
      const params: any = { page, page_size: 50 };
      if (filter === 'needs_review') {
        params.needs_review = true;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
        { headers, params }
      );
      
      setTransactions(Array.isArray(response.data?.transactions) ? response.data.transactions : []);
      setTotalPages(response.data?.pagination?.total_pages ?? 1);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [jobId, page, filter, getAuthHeader]);
  
  useEffect(() => {
    // Don't fetch until we have a resolved job ID
    if (!jobId) {
      return;
    }
    
    const fetchData = async () => {
      console.log('🔵 fetchData called, jobId:', jobId);
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch job status
        console.log('🔵 Fetching job status...');
        const statusRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}`,
          { headers }
        );
        console.log('🔵 Got job status:', statusRes.data);
        setJobStatus(statusRes.data);
        
        // Fetch transactions
        setLoading(true);
        const txnRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
          { headers, params: { page: 1, page_size: 50 } }
        );
        setTransactions(Array.isArray(txnRes.data?.transactions) ? txnRes.data.transactions : []);
        setTotalPages(txnRes.data?.pagination?.total_pages ?? 1);
        setLoading(false);
        
        // Fetch anomalies
        const anomRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/anomalies/${jobId}`,
          { headers }
        );
        setAnomalies(Array.isArray(anomRes.data) ? anomRes.data : []);
      } catch (err: any) {
        console.error('🔴 Failed to fetch data:', err);
        setError(err.response?.data?.detail || 'Failed to load data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [jobId, getToken]); // Only re-run when jobId changes
  
  // Auto-refresh when job is processing - faster polling during extraction
  useEffect(() => {
    const isProcessing = jobStatus?.status && ['pending', 'extracting', 'classifying'].includes(jobStatus.status);
    
    if (isProcessing) {
      // Poll faster during extraction phase for better UX
      const pollInterval = jobStatus?.status === 'extracting' ? 2000 : 5000;
      const interval = setInterval(() => {
        loadJobStatus();
      }, pollInterval);
      
      return () => clearInterval(interval);
    }
  }, [jobStatus?.status, loadJobStatus]);
  
  // Approve transaction
  const handleApprove = async (transactionId: string) => {
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/review`,
        { transaction_id: transactionId, action: 'approve' },
        { headers }
      );
      
      // Update local state
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { ...t, validation_status: 'approved' as const, needs_review: false }
          : t
      ));
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after approval
    } catch (err: any) {
      console.error('Failed to approve:', err);
    }
  };
  
  // Reclassify transaction
  const handleReclassify = async (transactionId: string, newCategory: string) => {
    try {
      const headers = await getAuthHeader();
      
      // Find the transaction to get original category
      const transaction = transactions.find(t => t.id === transactionId);
      const originalCategory = transaction?.classification?.category || 'Unknown';
      
      // Update the transaction
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/review`,
        { transaction_id: transactionId, action: 'modify', new_category: newCategory },
        { headers }
      );
      
      // Record the learning for AI feedback loop
      if (user?.id && transaction && originalCategory !== newCategory) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/v1/cleanup/learnings/record`,
            {
              user_id: user.id,
              description: transaction.description,
              payee: transaction.payee || null,
              original_category: originalCategory,
              corrected_category: newCategory,
              amount: transaction.amount,
              transaction_id: transactionId,
              job_id: jobId
            },
            { headers }
          );
          console.log('📚 AI Learning recorded:', transaction.description, '->', newCategory);
        } catch (learnErr) {
          // Don't fail the main operation if learning fails
          console.warn('Failed to record learning:', learnErr);
        }
      }
      
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { 
              ...t, 
              classification: { ...t.classification, category: newCategory, confidence: 1.0 },
              validation_status: 'modified' as const, 
              needs_review: false,
              learned: true  // Mark as learned from user correction
            }
          : t
      ));
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after reclassification
    } catch (err: any) {
      console.error('Failed to reclassify:', err);
    }
  };
  
  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/bulk-review`,
        { transaction_ids: Array.from(selectedIds), action: 'approve' },
        { headers }
      );
      
      setTransactions(prev => prev.map(t => 
        selectedIds.has(t.id)
          ? { ...t, validation_status: 'approved' as const, needs_review: false }
          : t
      ));
      setSelectedIds(new Set());
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after bulk approval
    } catch (err: any) {
      console.error('Failed to bulk approve:', err);
    }
  };
  
  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  // Select all - now loads ALL transactions if needed
  const toggleSelectAll = async () => {
    if (selectedIds.size === transactions.length && transactions.length > 0) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // If we have all transactions loaded, select them
      // Otherwise fetch all transaction IDs first
      if (jobStatus && transactions.length < jobStatus.stats.total_transactions) {
        try {
          const headers = await getAuthHeader();
          // Load all transactions (no pagination) to get all IDs
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
            { headers, params: { page_size: 1000 } } // Get all
          );
          const allTransactions = Array.isArray(response.data?.transactions) ? response.data.transactions : [];
          setTransactions(allTransactions);
          setSelectedIds(new Set(allTransactions.map((t: any) => t.id)));
        } catch (err) {
          console.error('Failed to load all transactions:', err);
          // Fall back to selecting visible only
          setSelectedIds(new Set(transactions.map(t => t.id)));
        }
      } else {
        setSelectedIds(new Set(transactions.map(t => t.id)));
      }
    }
  };
  
  // Export CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const headers = await getAuthHeader();
      
      // Use fetch API to avoid XMLHttpRequest responseType conflicts
      const response = await fetch(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/export?format=csv`,
        { 
          method: 'GET',
          headers: {
            ...headers,
            'Accept': 'text/csv'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cleanup_${jobId?.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Show completion modal if job is complete
      if (jobStatus?.stats.needs_review === 0) {
        setShowCompletionModal(true);
      }
    } catch (err: any) {
      console.error('Failed to export:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Mark job as complete
  const handleMarkComplete = async () => {
    // If there are still items needing review, confirm with user
    if (jobStatus && jobStatus.stats.needs_review > 0) {
      const confirmed = window.confirm(
        `You still have ${jobStatus.stats.needs_review} transaction(s) flagged for review.\n\n` +
        `Do you want to complete anyway?\n\n` +
        `• Approved items will be included in your report\n` +
        `• Unreviewed items will be auto-approved as-is`
      );
      if (!confirmed) return;
    }
    
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/complete`,
        {},
        { headers }
      );
      loadJobStatus();
      setShowCompletionModal(true);
    } catch (err: any) {
      console.error('Failed to mark complete:', err);
      setError('Failed to mark as complete. Please try again.');
    }
  };

  // Create report from cleanup data
  const handleCreateReport = async () => {
    try {
      const headers = await getAuthHeader();
      
      // Get wizard-ready financial data from cleanup
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/wizard-data`,
        { headers }
      );
      
      const wizardData = response.data;
      console.log('📊 Wizard data from cleanup:', wizardData);
      
      // Store in sessionStorage for wizard to pick up
      sessionStorage.setItem('cleanup_wizard_data', JSON.stringify({
        type: 'cleanup',
        job_id: jobId,
        financial_data: wizardData.financial_data,
        reconstructed_pl: wizardData.reconstructed_pl,  // P&L reconstruction data
        summary: wizardData.summary,
        metadata: wizardData.metadata
      }));
      
      // Clear wizard context since we're completing the flow
      sessionStorage.removeItem('wizard_context');
      
      // Navigate to wizard (or resume existing report)
      if (wizardContext?.report_id) {
        navigate(`/wizard-v2?resume=${wizardContext.report_id}`);
      } else {
        navigate('/wizard-v2');
      }
    } catch (err: any) {
      console.error('Failed to prepare report data:', err);
      setError('Failed to prepare report data. Please try again.');
    }
  };

  // Return to wizard without creating report (cancel cleanup flow)
  const handleReturnToWizard = () => {
    sessionStorage.removeItem('wizard_context');
    if (wizardContext?.report_id) {
      navigate(`/wizard-v2?resume=${wizardContext.report_id}`);
    } else {
      navigate('/wizard-v2');
    }
  };

  // Save job progress for later continuation
  const [saving, setSaving] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [businessNameInput, setBusinessNameInput] = useState('');
  
  const handleSaveForLater = async (overrideName?: string) => {
    // If no business name exists, prompt for one
    const currentBusinessName = overrideName || jobStatus?.business_name;
    
    if (!currentBusinessName && !showNamePrompt) {
      // Suggest a name from the filename if available
      const suggestedName = jobStatus?.stats ? 
        `Bank Statement Upload - ${new Date().toLocaleDateString()}` : '';
      setBusinessNameInput(suggestedName);
      setShowNamePrompt(true);
      return;
    }
    
    try {
      setSaving(true);
      const headers = await getAuthHeader();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/save-for-later`,
        { business_name: currentBusinessName || businessNameInput || 'Unnamed Job' },
        { headers }
      );
      
      console.log('💾 Job saved for later:', response.data);
      
      // Show success feedback briefly then navigate
      setShowCompletionModal(false);
      setShowNamePrompt(false);
      
      // Navigate to dashboard - job will appear there for resumption
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Failed to save job:', err);
      setError('Failed to save progress. Please try again.');
      setSaving(false);
    }
  };

  // Show processing state when job is still being processed
  // Check this BEFORE the loading state to show file progress during extraction
  const isProcessing = jobStatus?.status && ['pending', 'extracting', 'classifying'].includes(jobStatus.status);
  
  // DEBUG: Log the current state
  console.log('CleanupValidation render:', { jobStatus, isProcessing, loading, resolving, resolvedJobId, error });
  
  // Show resolving state when looking up job from draft ID
  if (resolving) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: 'var(--color-brand)' }}></div>
            <p className="mt-4 cs-body">Finding cleanup job for this case...</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show error state if no job found
  if (error && !jobId) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md p-8">
            <h2 className="cs-h2 mb-2">No Transaction Data Found</h2>
            <p className="cs-body mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/queue')}
                className="w-full px-4 py-2 font-medium rounded-lg transition-colors"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                ← Back to Work Queue
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2 font-medium rounded-lg transition-colors"
                style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show dedicated processing UI when job is still being processed
  if (isProcessing) {
    const totalFiles = (jobStatus as any)?.total_files || 0;
    const processedFiles = (jobStatus as any)?.processed_files || 0;
    const progressPct = jobStatus?.progress_pct || 0;
    
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-lg p-8">
            <div className="relative mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-4 mx-auto" style={{ borderColor: 'var(--color-needs-human-tint-bd)', borderTopColor: 'var(--color-brand)' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">📄</span>
              </div>
            </div>

            <h2 className="cs-h2 mb-2">Processing Your Documents</h2>

            {/* File count display */}
            {totalFiles > 0 && (
              <div className="mb-4 rounded-lg px-4 py-3" style={{ background: 'var(--color-needs-human-tint)' }}>
                <p className="cs-body font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                  {processedFiles} / {totalFiles} files processed
                </p>
                {/* Progress bar */}
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%`, background: 'var(--color-brand)' }}
                  />
                </div>
                <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{progressPct}% complete</p>
              </div>
            )}

            <p className="cs-body mb-6">
              {jobStatus?.status === 'extracting' && 'Extracting transactions from your bank statements...'}
              {jobStatus?.status === 'classifying' && 'Classifying and categorizing transactions...'}
              {jobStatus?.status === 'pending' && 'Preparing to process your files...'}
            </p>

            <div className="rounded-lg p-4 text-left mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h3 mb-2">What's happening:</h3>
              <ul className="cs-body space-y-1">
                <li className="flex items-center gap-2" style={{ color: jobStatus?.status === 'pending' ? 'var(--color-text-primary)' : 'var(--color-text-muted)', fontWeight: jobStatus?.status === 'pending' ? 600 : 400 }}>
                  {jobStatus?.status === 'pending' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="w-4 h-4" aria-hidden="true" />} Uploading files
                </li>
                <li className="flex items-center gap-2" style={{ color: jobStatus?.status === 'extracting' ? 'var(--color-text-primary)' : jobStatus?.status === 'pending' ? 'var(--color-text-dim)' : 'var(--color-text-muted)', fontWeight: jobStatus?.status === 'extracting' ? 600 : 400 }}>
                  {jobStatus?.status === 'extracting' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : jobStatus?.status === 'pending' ? <Clock className="w-4 h-4" aria-hidden="true" /> : <CheckCircle2 className="w-4 h-4" aria-hidden="true" />} Extracting transactions (OCR)
                  {jobStatus?.status === 'extracting' && totalFiles > 0 && (
                    <span className="ml-1 cs-meta">({processedFiles}/{totalFiles})</span>
                  )}
                </li>
                <li className="flex items-center gap-2" style={{ color: jobStatus?.status === 'classifying' ? 'var(--color-text-primary)' : ['pending', 'extracting'].includes(jobStatus?.status || '') ? 'var(--color-text-dim)' : 'var(--color-text-muted)', fontWeight: jobStatus?.status === 'classifying' ? 600 : 400 }}>
                  {jobStatus?.status === 'classifying' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : ['pending', 'extracting'].includes(jobStatus?.status || '') ? <Clock className="w-4 h-4" aria-hidden="true" /> : <CheckCircle2 className="w-4 h-4" aria-hidden="true" />} AI Classification
                </li>
                <li className="flex items-center gap-2" style={{ color: 'var(--color-text-dim)' }}>
                  <Clock className="w-4 h-4" aria-hidden="true" /> Ready for review
                </li>
              </ul>
            </div>

            <p className="cs-meta">
              This may take a few minutes for large uploads. You can leave this page and return later -
              your progress will be saved automatically.
            </p>

            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 cs-body font-medium"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              ← Return to Dashboard
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Fallback loading state when not processing but still loading data
  if (loading && !transactions.length && !isProcessing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: 'var(--color-brand)' }}></div>
            <p className="mt-4 cs-body">Loading transactions...</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // The main validation view reads jobStatus.stats.* throughout (needs_review,
  // total_transactions, auto_approved, reviewed, …). A degraded job payload
  // (jobStatus present but stats absent) would otherwise crash the entire page to
  // the error boundary. Render a styled unavailable state instead of the stats grid.
  if (!jobStatus || !jobStatus.stats) {
    return (
      <AppLayout>
        <div className="p-6">
          <div
            className="rounded-lg p-6 flex items-start gap-3"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
            data-testid="cleanup-stats-unavailable"
          >
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                Transaction summary unavailable
              </p>
              <p className="cs-meta mt-1" style={{ color: 'var(--color-text-muted)' }}>
                This cleanup job hasn&apos;t produced summary statistics yet. Refresh once processing completes.
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
      {/* Completion Modal - Case Data Checklist */}
      {showCompletionModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'transparent' }}>
                  <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--color-verified)' }} aria-hidden="true" />
                </div>
                <div>
                  <h2 className="cs-h2">Bank Statement Data Collected!</h2>
                  <p className="cs-meta">
                    {jobStatus?.stats?.total_transactions || 0} transactions processed from your bank statements
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Data Collected Summary */}
              <div className="rounded-lg p-4 mb-6" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
                <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                  Data Collected from Financial Statements
                </h3>
                <div className="grid grid-cols-2 gap-3 cs-body">
                  <div className="rounded p-2" style={{ background: 'var(--color-bg-surface)' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Transactions:</span>
                    <span className="ml-2 font-semibold" style={{ color: 'var(--color-verified)' }}>{jobStatus?.stats?.total_transactions || 0}</span>
                  </div>
                  <div className="rounded p-2" style={{ background: 'var(--color-bg-surface)' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Categories:</span>
                    <span className="ml-2 font-semibold" style={{ color: 'var(--color-verified)' }}>Auto-classified</span>
                  </div>
                </div>
                <p className="cs-meta mt-3" style={{ color: 'var(--color-verified)' }}>
                  Supports: Bank statements, Credit card statements, QuickBooks exports
                </p>
              </div>

              {/* What's Still Needed */}
              <div className="rounded-lg p-4 mb-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                  Additional Data (Optional - Improves Accuracy)
                </h3>
                <p className="cs-body mb-4" style={{ color: 'var(--color-needs-human-text)' }}>
                  Your transaction data will be used to reconstruct a P&L. For higher accuracy, consider adding:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 cs-body">
                    <span className="mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>⬜</span>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Credit Card Statements</span>
                      <p className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>Capture detailed expenses not in bank statements</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 cs-body">
                    <span className="mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>⬜</span>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Historical P&L or Tax Returns</span>
                      <p className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>For baseline comparison (can be estimated without)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 cs-body">
                    <span className="mt-0.5" style={{ color: 'var(--color-verified)' }}>✅</span>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Event Information</span>
                      <p className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>Type of disruption, start/end dates (required)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 cs-body">
                    <span className="mt-0.5" style={{ color: 'var(--color-verified)' }}>✅</span>
                    <div>
                      <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Business Details</span>
                      <p className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>Business type, location (required)</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Name Prompt Dialog */}
              {showNamePrompt && (
                <div className="mb-4 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                  <label className="block cs-label mb-2">
                    Name this job for easy identification:
                  </label>
                  <input
                    type="text"
                    value={businessNameInput}
                    onChange={(e) => setBusinessNameInput(e.target.value)}
                    placeholder="e.g., ABC Company Q1 2024"
                    className="w-full px-3 py-2 rounded-lg cs-body"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                    autoFocus
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleSaveForLater(businessNameInput)}
                      disabled={saving}
                      className="flex-1 px-4 py-2 font-medium rounded-lg transition-colors cs-body"
                      style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setShowNamePrompt(false)}
                      className="px-4 py-2 font-medium rounded-lg transition-colors cs-body"
                      style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleCreateReport}
                  className="w-full px-4 py-4 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Continue Building Case
                  <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-brand-hover)' }}>Add More Data</span>
                </button>

                <p className="cs-meta text-center">
                  You'll be guided through collecting the remaining information in the Case Builder wizard.
                </p>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleExportCSV}
                    disabled={exporting}
                    className="flex-1 px-4 py-2 font-medium rounded-lg transition-colors cs-body"
                    style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    {exporting ? 'Exporting...' : (<span className="flex items-center justify-center gap-1"><Download className="w-4 h-4" aria-hidden="true" /> Export CSV</span>)}
                  </button>
                  <button
                    onClick={() => handleSaveForLater()}
                    disabled={saving}
                    className="flex-1 px-4 py-2 font-medium rounded-lg transition-colors cs-body"
                    style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    {saving ? 'Saving...' : 'Save & Continue Later'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Mobile Header */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3">
              <h1 className="cs-h2">Transaction Validation</h1>
              <button
                onClick={() => navigate('/cleanup')}
                className="p-2"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <p className="cs-meta">Job: {jobId?.slice(0, 8)}...</p>
              {jobStatus?.report_id && (
                <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                  {jobStatus.report_name || 'Report'}
                </span>
              )}
            </div>

            {/* Mobile Action Buttons */}
            <div className="flex gap-2">
              {jobStatus && (
                <button
                  onClick={handleMarkComplete}
                  className="flex-1 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
                  style={
                    jobStatus.stats.needs_review === 0
                      ? { background: 'var(--color-verified)', color: 'var(--color-bg-base)' }
                      : { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  }
                >
                  {jobStatus.stats.needs_review === 0 ? (
                    <><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Complete</>
                  ) : (
                    <><Zap className="w-4 h-4" aria-hidden="true" /> Complete ({jobStatus.stats.needs_review})</>
                  )}
                </button>
              )}
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="px-4 py-3 font-medium rounded-lg"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
              >
                <Download className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between">
            <div>
              <h1 className="cs-display">Transaction Validation</h1>
              <div className="flex items-center gap-3">
                <p className="cs-meta">Job: {jobId?.slice(0, 8)}...</p>
                {jobStatus?.report_id && (
                  <span className="cs-chip px-2 py-1 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                    Linked to: {jobStatus.report_name || 'Report'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Progress Indicator */}
              {jobStatus && (
                <div className="flex items-center gap-2 cs-body">
                  <span style={{ color: 'var(--color-text-muted)' }}>Progress:</span>
                  <span className="font-bold" style={{ color: jobStatus.progress_pct >= 100 ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                    {jobStatus.progress_pct}%
                  </span>
                  <span style={{ color: 'var(--color-text-dim)' }}>
                    {/* K3 Bug 2: numerator is `reviewed` ALONE. Backend `_update_job_stats`
                        (cleanup.py) sets reviewed = approved+modified+rejected and auto_approved
                        = approved-with-auto_approved_at — so auto_approved ⊆ reviewed. Adding them
                        double-counted every auto-approved txn (e.g. 26/18). reviewed already
                        counts auto-approved and is always ≤ total. */}
                    ({jobStatus.stats.reviewed || 0} / {jobStatus.stats.total_transactions})
                  </span>
                </div>
              )}

              {/* Export Button */}
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="px-4 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: 'var(--color-text-muted)' }}></div>
                    Exporting...
                  </>
                ) : (
                  <><Download className="w-4 h-4" aria-hidden="true" /> Export CSV</>
                )}
              </button>

              {/* PRIMARY ACTION: Complete & Return to Report Button - ALWAYS VISIBLE */}
              {jobStatus && (
                <button
                  onClick={handleMarkComplete}
                  className="px-5 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-lg"
                  style={
                    jobStatus.stats.needs_review === 0
                      ? { background: 'var(--color-verified)', color: 'var(--color-bg-base)' }
                      : { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  }
                  title={
                    jobStatus.stats.needs_review === 0
                      ? 'Complete cleanup and continue to report'
                      : `${jobStatus.stats.needs_review} items still need review - click to complete anyway`
                  }
                >
                  {jobStatus.stats.needs_review === 0 ? (
                    <><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Complete & Return to Report</>
                  ) : (
                    <><Zap className="w-4 h-4" aria-hidden="true" /> Complete ({jobStatus.stats.needs_review} pending)</>
                  )}
                </button>
              )}

              <button
                onClick={() => navigate('/cleanup')}
                className="px-4 py-2 rounded-lg"
                style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}
              >
                ← Back to Jobs
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      {jobStatus && (
        <div style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="text-center">
                <div className="cs-figure-lg">{jobStatus.stats.total_transactions}</div>
                <div className="cs-meta">Total</div>
              </div>
              <div className="text-center">
                <div className="cs-figure-lg" style={{ color: 'var(--color-verified)' }}>{jobStatus.stats.auto_approved}</div>
                <div className="cs-meta">Auto-Approved</div>
              </div>
              <div className="text-center">
                <div className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>{jobStatus.stats.needs_review}</div>
                <div className="cs-meta">Needs Review</div>
              </div>
              <div className="text-center">
                <div className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>{jobStatus.stats.reviewed || 0}</div>
                <div className="cs-meta">Reviewed</div>
              </div>
              <div className="text-center">
                <div className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>{jobStatus.progress_pct}%</div>
                <div className="cs-meta">Complete</div>
              </div>
              <div className="text-center">
                <span className="cs-chip px-3 py-1 rounded-full" style={
                  jobStatus.status === 'completed' ? { background: 'transparent', color: 'var(--color-verified)' } :
                  jobStatus.status === 'pending_review' ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                  { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                }>
                  {jobStatus.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
              <div
                className="h-full transition-all duration-300"
                style={{ width: `${jobStatus.progress_pct}%`, background: 'var(--color-brand)' }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Search & Filter Bar */}
      <div className="shadow-sm" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search descriptions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg cs-body"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5" style={{ color: 'var(--color-text-dim)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 rounded-lg cs-body"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Toggle Filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 rounded-lg flex items-center gap-2 cs-body"
              style={
                showFilters
                  ? { background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }
                  : { border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }
              }
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>

            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-2 pl-2" style={{ borderLeft: '1px solid var(--color-border)' }}>
                <span className="cs-body" style={{ color: 'var(--color-text-muted)' }}>{selectedIds.size} selected</span>
                <button
                  onClick={() => {
                    // Bulk approve logic would go here
                    console.log(`Bulk approve ${selectedIds.size} transactions`);
                  }}
                  className="px-3 py-1 rounded-lg cs-body"
                  style={{ background: 'transparent', color: 'var(--color-verified)' }}
                >
                  ✓ Approve All
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-2 py-1 cs-body"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 flex flex-wrap items-center gap-4" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
              <div className="flex items-center gap-2">
                <label className="cs-body" style={{ color: 'var(--color-text-muted)' }}>Amount:</label>
                <input
                  type="number"
                  placeholder="Min"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  className="w-24 px-2 py-1 rounded cs-body"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                />
                <span style={{ color: 'var(--color-text-dim)' }}>-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  className="w-24 px-2 py-1 rounded cs-body"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                />
              </div>

              <button
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('');
                  setAmountMin('');
                  setAmountMax('');
                }}
                className="cs-body"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Anomaly Banner - Always show */}
      <div style={unresolvedAnomalies.length > 0
        ? { background: 'var(--color-needs-human-tint)', borderBottom: '1px solid var(--color-needs-human-tint-bd)' }
        : { background: 'transparent', borderBottom: '1px solid var(--color-verified)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {unresolvedAnomalies.length > 0
                ? <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} aria-hidden="true" />
                : <CheckCircle2 className="w-6 h-6 flex-shrink-0" style={{ color: 'var(--color-verified)' }} aria-hidden="true" />}
              <div>
                {unresolvedAnomalies.length > 0 ? (
                  <>
                    <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                      {unresolvedAnomalies.length} Anomal{unresolvedAnomalies.length === 1 ? 'y' : 'ies'} Detected
                    </span>
                    <span className="cs-body ml-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      ({unresolvedAnomalies.filter(a => a.severity === 'critical' || a.severity === 'high').length} high priority)
                    </span>
                  </>
                ) : (
                  <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>
                    No Anomalies Detected
                  </span>
                )}
              </div>
            </div>
            {unresolvedAnomalies.length > 0 && (
              <button
                onClick={() => setShowAnomalies(!showAnomalies)}
                className="px-3 py-1 cs-body rounded-lg transition-colors"
                style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
              >
                {showAnomalies ? 'Hide Details' : 'View Details'}
              </button>
            )}
          </div>
          
          {/* Expanded Anomaly List */}
          {showAnomalies && unresolvedAnomalies.length > 0 && (
            <div className="mt-4 space-y-3">
              {unresolvedAnomalies.slice(0, showAllAnomalies ? unresolvedAnomalies.length : 10).map((anomaly, idx) => (
                <div
                  key={anomaly.id || idx}
                  className="p-3 rounded-lg border"
                  /* Severity → color from the ONE shared source (utils/anomalySeverity):
                     neutral card, crimson chip for critical/high. Same rule every surface uses. */
                  style={anomalySeverityStyle(anomaly.severity).card}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="cs-chip px-2 py-0.5 rounded" style={anomalySeverityStyle(anomaly.severity).chip}>
                          {anomaly.severity?.toUpperCase()}
                        </span>
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{anomaly.title}</span>
                      </div>
                      <p className="cs-body mt-1">{anomaly.description}</p>
                      {anomaly.explanation && (
                        <p className="cs-meta mt-2 leading-relaxed">{anomaly.explanation}</p>
                      )}
                      {/* Show related transaction info if available */}
                      {anomaly.related_amount && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="cs-meta">Amount:</span>
                          <span className="cs-figure font-medium"><FinancialValue value={anomaly.related_amount} cents /></span>
                          {anomaly.transaction_id && (
                            <button
                              onClick={async () => {
                                // Set the highlight
                                setHighlightedTransactionId(anomaly.transaction_id);
                                setFilter('all');
                                
                                // Fetch the page containing the target transaction
                                try {
                                  const headers = await getAuthHeader();
                                  const response = await axios.get(
                                    `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
                                    { 
                                      headers, 
                                      params: { 
                                        transaction_id: anomaly.transaction_id,
                                        page_size: 50 
                                      } 
                                    }
                                  );
                                  
                                  if (response.data?.transactions?.length > 0) {
                                    // Backend returns the page containing the target transaction
                                    setTransactions(Array.isArray(response.data?.transactions) ? response.data.transactions : []);
                                    // Update page to match the returned page from backend
                                    setPage(response.data.pagination?.page || 1);
                                    setTotalPages(response.data.pagination?.total_pages || 1);
                                    
                                    // Scroll to the transaction after render
                                    setTimeout(() => {
                                      const el = document.getElementById(`txn-${anomaly.transaction_id}`);
                                      if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      } else {
                                        // Scroll to transactions table
                                        const tableEl = document.querySelector('[data-transactions-table]');
                                        if (tableEl) tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }
                                      
                                      // Auto-clear highlight after 5 seconds
                                      setTimeout(() => {
                                        setHighlightedTransactionId(null);
                                      }, 5000);
                                    }, 300);
                                  }
                                } catch (err) {
                                  console.error('Error fetching transaction:', err);
                                  // Fallback - scroll to transactions area
                                  const tableEl = document.querySelector('[data-transactions-table]');
                                  if (tableEl) tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              className="cs-meta underline"
                              style={{ color: 'var(--color-needs-human-text)' }}
                            >
                              View Transaction
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {unresolvedAnomalies.length > 10 && (
                <button
                  onClick={() => setShowAllAnomalies(!showAllAnomalies)}
                  className="w-full py-2 cs-body rounded-lg transition-colors font-medium"
                  style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                >
                  {showAllAnomalies
                    ? 'Show Less'
                    : `Show ${unresolvedAnomalies.length - 10} More Anomalies`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 rounded-lg cs-body"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
            >
              <option value="needs_review">Needs Review</option>
              <option value="all">All Transactions</option>
              <option value="approved">Approved</option>
            </select>

            {/* Bulk actions */}
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkApprove}
                className="px-4 py-2 rounded-lg cs-body cursor-pointer"
                style={{ background: 'var(--color-verified)', color: 'var(--color-bg-base)' }}
              >
                Approve Selected ({selectedIds.size})
              </button>
            )}
          </div>

          <div className="cs-meta">
            Page {page} of {totalPages}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-4">
          <div className="px-4 py-3 rounded-lg cs-body" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
            {error}
          </div>
        </div>
      )}
      
      {/* Transaction Table */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden md:block rounded-lg shadow overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }} data-transactions-table>
          <table className="w-full">
            <thead style={{ background: 'var(--color-bg-base)' }}>
              <tr>
                <th className="py-3 px-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === transactions.length && transactions.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                    style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
                  />
                </th>
                <th className="py-3 px-4 text-left cs-label">Date</th>
                <th className="py-3 px-4 text-left cs-label">Description</th>
                <th className="py-3 px-4 text-left cs-label">Amount</th>
                <th className="py-3 px-4 text-left cs-label">Category</th>
                <th className="py-3 px-4 text-left cs-label">Confidence</th>
                <th className="py-3 px-4 text-left cs-label">Status</th>
                <th className="py-3 px-4 text-left cs-label">Action</th>
              </tr>
            </thead>
            <tbody>
              {transactions
                .filter(txn => {
                  // Search filter
                  if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) {
                    return false;
                  }
                  // Category filter
                  if (categoryFilter && txn.classification?.category !== categoryFilter) {
                    return false;
                  }
                  // Amount range filter
                  const amount = Math.abs(txn.amount);
                  if (amountMin && amount < parseFloat(amountMin)) {
                    return false;
                  }
                  if (amountMax && amount > parseFloat(amountMax)) {
                    return false;
                  }
                  return true;
                })
                .map(txn => (
                <TransactionRow
                  key={txn.id}
                  transaction={txn}
                  selected={selectedIds.has(txn.id)}
                  highlighted={highlightedTransactionId === txn.id}
                  onSelect={toggleSelect}
                  onApprove={handleApprove}
                  onReclassify={handleReclassify}
                />
              ))}
            </tbody>
          </table>
          
          {transactions.length === 0 && (
            <div className="text-center py-12 cs-body" style={{ color: 'var(--color-text-muted)' }}>
              No transactions found
            </div>
          )}
        </div>

        {/* Mobile Cards - Visible only on mobile */}
        <div className="md:hidden">
          {/* Mobile Header with Select All */}
          <div className="px-4 py-3 flex items-center justify-between sticky top-0 z-10" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border-sub)' }}>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedIds.size === transactions.length && transactions.length > 0}
                onChange={toggleSelectAll}
                className="rounded w-5 h-5"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
              />
              <span className="cs-body" style={{ color: 'var(--color-text-muted)' }}>Select All</span>
            </div>
            <span className="cs-meta">
              {transactions.filter(txn => {
                if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (categoryFilter && txn.classification?.category !== categoryFilter) return false;
                const amount = Math.abs(txn.amount);
                if (amountMin && amount < parseFloat(amountMin)) return false;
                if (amountMax && amount > parseFloat(amountMax)) return false;
                return true;
              }).length} transactions
            </span>
          </div>

          {/* Mobile Swipe Instructions */}
          <div className="px-4 py-2 flex items-center justify-center gap-4 cs-meta" style={{ background: 'var(--color-bg-elevated)' }}>
            <span className="flex items-center gap-1" style={{ color: 'var(--color-verified)' }}>
              <span>👉</span> Swipe right to approve
            </span>
            <span style={{ color: 'var(--color-text-dim)' }}>|</span>
            <span className="flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
              Swipe left to edit <span>👈</span>
            </span>
          </div>

          {/* Transaction Cards */}
          <div style={{ background: 'var(--color-bg-base)' }}>
            {transactions
              .filter(txn => {
                if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (categoryFilter && txn.classification?.category !== categoryFilter) return false;
                const amount = Math.abs(txn.amount);
                if (amountMin && amount < parseFloat(amountMin)) return false;
                if (amountMax && amount > parseFloat(amountMax)) return false;
                return true;
              })
              .map(txn => (
                <TransactionCard
                  key={txn.id}
                  transaction={txn}
                  selected={selectedIds.has(txn.id)}
                  highlighted={highlightedTransactionId === txn.id}
                  onSelect={toggleSelect}
                  onApprove={handleApprove}
                  onReclassify={handleReclassify}
                />
              ))
            }
          </div>
          
          {transactions.length === 0 && (
            <div className="text-center py-12 cs-body" style={{ color: 'var(--color-text-muted)', background: 'var(--color-bg-surface)' }}>
              No transactions found
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded disabled:opacity-50 cursor-pointer cs-body"
              style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              Previous
            </button>
            <span className="px-3 py-1 cs-body">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 rounded disabled:opacity-50 cursor-pointer cs-body"
              style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              Next
            </button>
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  );
}
